<script setup lang="ts">
import type { Components } from '@/api/generated/api';
import ExternalMaterialIcon from '@/assets/icons/ExternalMaterialIcon.vue';
import ComposerChip from '@/core/elements/ComposerChip.vue';
import ExternalMaterialSelectionPopover from '@/core/supportMeasures/ExternalMaterialSelectionPopover.vue';
import { areArraysEqualUnordered } from 'common/utils/array.utils';
import { computed, ref } from 'vue';

const props = defineProps<{
  materials: Components.Schemas.ExternalMaterialLinkDto[];
  schoolSubjectId: number | null;
  schoolClassId: number | null;
  disabled?: boolean;
  noteTone?: boolean;
}>();
const emit = defineEmits<{ 'update:materials': [ids: number[]] }>();
const anchor = ref<HTMLElement | null>(null);
const popover = ref<InstanceType<typeof ExternalMaterialSelectionPopover>>();
const selectedMaterials = computed(() =>
  props.materials.map((item) => ({ externalMaterialId: item.externalMaterialId, item })),
);
const toggle = (element: HTMLElement): void => {
  anchor.value = element;
  if (popover.value?.isOpen) popover.value.hide();
  else void popover.value?.show();
};
const setMaterials = (ids: number[]): void => {
  if (
    props.disabled ||
    areArraysEqualUnordered(
      ids,
      props.materials.map((item) => item.externalMaterialId),
    )
  )
    return;
  emit('update:materials', ids);
};
const hide = (): void => popover.value?.hide();
defineExpose({ hide });
</script>

<template>
  <ComposerChip
    appearance="ghost"
    :class="noteTone ? '!bg-transparent !text-note-accent !border-note note-state-effects' : undefined"
    :label="$t('supportMeasureEntry.materials.appChip')"
    :disabled="disabled"
    :expanded="!!popover?.isOpen"
    @click="toggle"
  >
    <template #icon><ExternalMaterialIcon class="size-icon-smd shrink-0" /></template>
  </ComposerChip>
  <ExternalMaterialSelectionPopover
    ref="popover"
    :anchor-element="anchor"
    commit-on-close
    mobile-drawer
    :school-subject-id="schoolSubjectId"
    :school-class-id="schoolClassId"
    :selected-materials="selectedMaterials"
    @update:materials="setMaterials"
  />
</template>
